import { router } from "../../router";
import { useAccessStore } from "../store/useAccessStore";

/** 서버와 합의한 푸시 type Enum. */
type PushTargetType =
  | "COMPETITION"
  | "SCORE"
  | "COMMUNITY"
  | "SETTING_INFO"
  | "SETTING_OPTIONAL_TERMS"
  | "SETTING_TOURNAMENT_HISTORY"
  | "NOTICE";

interface ParsedPushMessage {
  type: PushTargetType;
  /** 상세 화면 이동에 필요한 id. SETTING_TOURNAMENT_HISTORY에서는 대회 ID. */
  id?: number | string;
  /** SETTING_TOURNAMENT_HISTORY 전용 접수 ID. */
  applyId?: number | string;
}

interface PushMessageData {
  message: string;
}

declare global {
  interface Window {
    handlePushMessage: (data: PushMessageData | string) => void;
  }
}

/** 비로그인 상태에서도 진입할 수 있는 type. */
const PUBLIC_PUSH_TYPES = new Set<PushTargetType>(["COMPETITION", "COMMUNITY"]);

/** 푸시 payload를 이동할 경로로 변환한다. 이동이 불필요하면 null. */
function resolveTargetPath(parsed: ParsedPushMessage): string | null {
  switch (parsed.type) {
    case "COMPETITION":
      return parsed.id != null ? `/competition/${parsed.id}` : null;

    case "SCORE":
      return parsed.id != null ? `/score/${parsed.id}` : null;

    case "COMMUNITY":
      return parsed.id != null ? `/community/${parsed.id}` : null;

    case "SETTING_INFO":
      return "/setting/info";

    case "SETTING_OPTIONAL_TERMS":
      return "/setting/optional-terms";

    case "SETTING_TOURNAMENT_HISTORY":
      return parsed.id != null && parsed.applyId != null
        ? `/setting/tournament-history/${parsed.id}/${parsed.applyId}`
        : null;

    case "NOTICE":
      return parsed.id != null ? `/notice/${parsed.id}` : null;

    default:
      return null;
  }
}

/**
 * 네이티브에서 푸시 알림을 눌렀을 때 호출하는 App -> Web 브릿지.
 *
 * 호출 형식: handlePushMessage({ message: "{\"type\":\"COMPETITION\",\"id\":70}" })
 */
window.handlePushMessage = (data: PushMessageData | string): void => {
  try {
    const message = typeof data === "string" ? data : data?.message;
    if (!message) {
      console.error("handlePushMessage: message가 없습니다.", data);
      return;
    }

    const parsed = JSON.parse(message) as ParsedPushMessage;
    const targetPath = resolveTargetPath(parsed);

    if (!targetPath) {
      console.error("handlePushMessage: 이동할 경로를 찾지 못했습니다.", parsed);
      return;
    }

    // 로그인이 필요한 화면인데 비로그인 상태면 홈으로 보낸다.
    if (
      !PUBLIC_PUSH_TYPES.has(parsed.type) &&
      !useAccessStore.getState().isAuthenticated()
    ) {
      router.navigate("/");
      return;
    }

    router.navigate(targetPath);
  } catch (error) {
    console.error(error, "handlePushMessage 오류");
  }
};

export {};
